import React, { useState, useEffect, ReactNode } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  DragIndicator,
  ExpandLess,
  ExpandMore,
  Settings,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export interface DashboardSection {
  id: string;
  title: string;
  component: ReactNode;
  collapsed?: boolean;
  visible?: boolean;
}

interface DraggableDashboardProps {
  sections: DashboardSection[];
  onSectionOrderChange?: (newOrder: string[]) => void;
  storageKey?: string;
}

const DraggableDashboard: React.FC<DraggableDashboardProps> = ({
  sections: initialSections,
  onSectionOrderChange,
  storageKey = 'dashboardOrder'
}) => {
  const [sections, setSections] = useState<DashboardSection[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load saved order and collapsed states from localStorage
  useEffect(() => {
    if (!initialSections || initialSections.length === 0) {
      return;
    }

    const savedState = localStorage.getItem(storageKey);
    if (savedState) {
      try {
        const { order, collapsed } = JSON.parse(savedState);

        // Reorder sections based on saved order
        const orderedSections = order
          .map((id: string) => {
            const section = initialSections.find(s => s.id === id);
            if (section) {
              // Apply saved collapsed state
              return {
                ...section,
                collapsed: collapsed[id] || false
              };
            }
            return null;
          })
          .filter(Boolean);

        // Add any new sections that weren't in saved order
        const savedIds = new Set(order);
        const newSections = initialSections.filter(s => !savedIds.has(s.id));

        setSections([...orderedSections, ...newSections]);
      } catch (error) {
        console.error('Error loading dashboard layout:', error);
        setSections(initialSections);
      }
    } else {
      // No saved state, use initial sections
      setSections(initialSections);
    }
    setIsInitialized(true);
  }, [initialSections, storageKey]);

  // Save order and collapsed states to localStorage
  const saveLayout = (newSections: DashboardSection[]) => {
    const order = newSections.map(s => s.id);
    const collapsed = newSections.reduce((acc, section) => {
      acc[section.id] = section.collapsed || false;
      return acc;
    }, {} as Record<string, boolean>);

    localStorage.setItem(storageKey, JSON.stringify({ order, collapsed }));

    if (onSectionOrderChange) {
      onSectionOrderChange(order);
    }
  };

  const handleDragEnd = (result: any) => {
    setIsDragging(false);

    if (!result.destination) {
      return;
    }

    const items = Array.from(sections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSections(items);
    saveLayout(items);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const toggleCollapse = (sectionId: string) => {
    const newSections = sections.map(section =>
      section.id === sectionId
        ? { ...section, collapsed: !section.collapsed }
        : section
    );
    setSections(newSections);
    saveLayout(newSections);
  };

  const resetLayout = () => {
    localStorage.removeItem(storageKey);
    setSections(initialSections);
    saveLayout(initialSections);
  };

  // Ensure we have a unique key for react-beautiful-dnd
  const [uniqueKey] = useState(() => Math.random().toString(36).substr(2, 9));

  // Don't render until initialized
  if (!isInitialized || sections.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Settings button to reset layout */}
      <Box sx={{ position: 'absolute', top: -50, right: 0, zIndex: 1 }}>
        <Tooltip title="Reset dashboard layout">
          <IconButton onClick={resetLayout} size="small">
            <Settings />
          </IconButton>
        </Tooltip>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <Droppable droppableId={`dashboard-${uniqueKey}`}>
          {(provided) => (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                transition: isDragging ? 'none' : 'all 0.3s ease',
              }}
            >
              {sections.map((section, index) => (
                <Draggable
                  key={section.id}
                  draggableId={section.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 3,
                        position: 'relative',
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        boxShadow: snapshot.isDragging ? 6 : 1,
                        transform: snapshot.isDragging
                          ? `${provided.draggableProps.style?.transform} rotate(1deg)`
                          : provided.draggableProps.style?.transform,
                        transition: snapshot.isDragging ? 'none' : 'all 0.2s ease',
                        backgroundColor: snapshot.isDragging
                          ? 'background.paper'
                          : 'transparent',
                        ...provided.draggableProps.style,
                      }}
                    >
                      {/* Drag Handle and Controls */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1,
                          borderBottom: section.collapsed ? 'none' : 1,
                          borderColor: 'divider',
                          cursor: 'move',
                          backgroundColor: 'background.paper',
                          borderRadius: '8px 8px 0 0',
                        }}
                        {...provided.dragHandleProps}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DragIndicator
                            sx={{
                              color: 'text.secondary',
                              opacity: 0.5,
                              '&:hover': { opacity: 1 }
                            }}
                          />
                          <Typography variant="subtitle1" fontWeight="medium">
                            {section.title}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapse(section.id);
                          }}
                        >
                          {section.collapsed ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                      </Box>

                      {/* Section Content */}
                      <Box
                        sx={{
                          display: section.collapsed ? 'none' : 'block',
                          animation: section.collapsed ? 'none' : 'fadeIn 0.3s ease',
                          '@keyframes fadeIn': {
                            from: { opacity: 0 },
                            to: { opacity: 1 },
                          },
                        }}
                      >
                        {section.component}
                      </Box>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default DraggableDashboard;